import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { eventApi } from '../services/api';

export interface PublicLanguage {
  code: string;
  name: string;
  flag: string | null;
  is_default: 0 | 1 | boolean;
}

interface TranslationContextValue {
  /** Lingua attiva (null = non ancora risolta: si usa quella dell'evento) */
  lang: string | null;
  setLang: (code: string) => void;
  /** Evento mono-lingua: forza la lingua di default ignorando la preferenza salvata */
  setForcedDefault: (forced: boolean) => void;
  /** Sincronizza la lingua risolta dal backend senza marcare una scelta utente */
  syncResolved: (code: string, available?: PublicLanguage[]) => void;
  languages: PublicLanguage[];
  /** Traduzione: dizionario dal BE, con l'italiano inline come fallback garantito */
  t: (key: string, fallback: string, vars?: Record<string, string | number>) => string;
  /** Locale per date/valuta coerente con la lingua attiva */
  locale: string;
  fmtDate: (date: string | Date | null | undefined) => string;
  fmtMoney: (amount: number) => string;
}

const LOCALES: Record<string, string> = { it: 'it-IT', en: 'en-GB' };
const STORAGE_KEY = 'public_lang';

const TranslationContext = createContext<TranslationContextValue | null>(null);

const interpolate = (text: string, vars?: Record<string, string | number>): string => {
  if (!vars) return text;
  return Object.entries(vars).reduce(
    (acc, [k, v]) => acc.split(`{{${k}}}`).join(String(v)),
    text
  );
};

const initialLang = (): string | null => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (fromUrl) return fromUrl;
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<string | null>(initialLang);
  const [languages, setLanguages] = useState<PublicLanguage[]>([]);
  const [dict, setDict] = useState<Record<string, string>>({});
  const [forcedDefault, setForcedDefault] = useState(false);

  // Lingua effettiva: sugli eventi mono-lingua si resta sulla default
  const defaultCode = languages.find(l => l.is_default)?.code ?? 'it';
  const effectiveLang = forcedDefault ? defaultCode : lang;

  // Lingue abilitate: servono anche nelle pagine senza evento (pay-by-link)
  useEffect(() => {
    let alive = true;
    eventApi.getLanguages()
      .then(ls => { if (alive && Array.isArray(ls)) setLanguages(ls); })
      .catch(() => { /* selettore nascosto */ });
    return () => { alive = false; };
  }, []);

  // Dizionario dei termini per la lingua effettiva
  useEffect(() => {
    if (!effectiveLang) return;
    let alive = true;
    eventApi.getTranslations(effectiveLang)
      .then(res => { if (alive) setDict(res.terms ?? {}); })
      .catch(() => { /* fallback inline: la UI resta in italiano */ });
    return () => { alive = false; };
  }, [effectiveLang]);

  const setLang = useCallback((code: string) => {
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
      const url = new URL(window.location.href);
      url.searchParams.set('lang', code);
      window.history.replaceState({}, '', url.toString());
    } catch { /* storage non disponibile */ }
  }, []);

  const syncResolved = useCallback((code: string, available?: PublicLanguage[]) => {
    if (available?.length) setLanguages(available);
    setLangState(prev => prev ?? code);
  }, []);

  const t = useCallback(
    (key: string, fallback: string, vars?: Record<string, string | number>) =>
      interpolate(dict[key] ?? fallback, vars),
    [dict]
  );

  const locale = LOCALES[effectiveLang ?? 'it'] ?? effectiveLang ?? 'it-IT';

  const fmtDate = useCallback((date: string | Date | null | undefined) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(locale).format(d);
  }, [locale]);

  const fmtMoney = useCallback(
    (amount: number) => new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(amount),
    [locale]
  );

  const value = useMemo(
    () => ({ lang: effectiveLang, setLang, setForcedDefault, syncResolved, languages, t, locale, fmtDate, fmtMoney }),
    [effectiveLang, setLang, syncResolved, languages, t, locale, fmtDate, fmtMoney]
  );

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};

export const useTranslation = (): TranslationContextValue => {
  const ctx = useContext(TranslationContext);
  if (!ctx) throw new Error('useTranslation richiede TranslationProvider');
  return ctx;
};
